import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, HostBinding, inject, input, OnInit, output, signal} from '@angular/core';
import {FormArray, FormBuilder, FormControl} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatOptionModule} from '@angular/material/core';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatListModule} from '@angular/material/list';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSelectModule} from '@angular/material/select';
import {select, Store} from '@ngrx/store';
import {take} from 'rxjs/operators';
import {RemoveUserConflictResponse} from 'src/app/core/model/project';
import {
  AssignUserOrTeamToProjectAction,
  RemoveUserOrTeamFromProjectAction,
} from 'src/app/core/store/projects/projects.action';
import {selectProjectsMap} from 'src/app/core/store/projects/projects.selector';
import {OphAvatarComponent} from 'src/app/shared/design/oph-avatar/oph-avatar.component';
import {OphBackButtonComponent} from 'src/app/shared/design/oph-back-button/oph-back-button.component';
import {
  OphButtonGroupComponent,
  OphButtonGroupOption,
} from 'src/app/shared/design/oph-button-group/oph-button-group.component';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {Project} from '../../core/model/project';
import {User} from '../../core/model/user';
import {Team} from '../../teams/shared/model/team';

export interface UsersTeamsSelectionData {
  projectId: string;
  users: User[];
  usersMap: Record<string, User>;
  teams: Team[];
  teamsMap: Record<string, Team>;
}

export interface UsersTeamsSelectionResult {
  users: string[];
  teams: string[];
  usersMap: Record<string, User>;
  teamsMap: Record<string, Team>;
}

@Component({
  selector: 'projects-users-teams-selection',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatCheckboxModule,
    MatListModule,
    MatProgressSpinnerModule,
    OphBackButtonComponent,
    OphButtonGroupComponent,
    OphAvatarComponent,
    OphButtonModule,
  ],
  templateUrl: './projects-users-teams-selection.component.html',
  styleUrl: './projects-users-teams-selection.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsUsersTeamsSelectionComponent implements OnInit {
  @HostBinding('class.oph-scroll-bar') readonly ophScrollBar = true;

  private readonly fb = inject(FormBuilder);
  readonly store$ = inject(Store);

  readonly data = input.required<UsersTeamsSelectionData>();
  readonly title = input<string>('Assign to Project');
  readonly showBackButton = input<boolean>(true);

  readonly backButtonClick = output<void>();
  readonly confirmRemove = output<{userId?: string; teamId?: string; force: boolean}>();
  readonly modifyUserTeamList = output<{type: 'user' | 'team'; action: 'add' | 'remove'; id: string}>();

  readonly projectMap$ = this.store$.pipe(select(selectProjectsMap));

  readonly form = this.fb.group({
    users: this.fb.array<string>([]),
    teams: this.fb.array<string>([]),
  });

  readonly availableUsers = signal<User[]>([]);
  readonly availableTeams = signal<Team[]>([]);
  readonly project = signal<Project>(null);
  loadingObject = signal<Record<string, boolean>>({});
  selectedOption = signal<string>('people');
  showOverlay = signal<boolean>(false);
  conflictResponse = signal<RemoveUserConflictResponse>(null);

  readonly options: OphButtonGroupOption[] = [
    {name: 'people', display: 'People'},
    {name: 'teams', display: 'Teams'},
  ];

  ngOnInit(): void {
    this.initialize();
  }

  private initialize(): void {
    // Set available users and teams from input data
    this.availableUsers.set(this.data().users);
    this.availableTeams.set(this.data().teams);

    this.projectMap$.pipe(take(1)).subscribe(projectMap => {
      const project = projectMap[this.data().projectId];
      if (project) {
        this.project.set(project);
        this.initializeForm();
      }
    });
  }

  get usersFormArray(): FormArray<FormControl<string>> {
    return this.form.get('users') as FormArray<FormControl<string>>;
  }

  get teamsFormArray(): FormArray<FormControl<string>> {
    return this.form.get('teams') as FormArray<FormControl<string>>;
  }

  private initializeForm(): void {
    this.usersFormArray.clear();
    this.teamsFormArray.clear();

    const projectUsers = this.project().users || [];
    projectUsers.forEach(userId => {
      this.usersFormArray.push(this.fb.control(userId, {nonNullable: true}));
    });

    const projectTeams = this.project().teams || [];
    projectTeams.forEach(teamId => {
      this.teamsFormArray.push(this.fb.control(teamId, {nonNullable: true}));
    });
  }

  onBackButtonClick(): void {
    this.backButtonClick.emit();
  }

  onOptionChange(option: string): void {
    this.selectedOption.set(option);
  }

  isUserSelected(userId: string): boolean {
    return this.usersFormArray.value.includes(userId);
  }

  isTeamSelected(teamId: string): boolean {
    return this.teamsFormArray.value.includes(teamId);
  }

  toggleUser(userId: string): void {
    const currentIndex = this.usersFormArray.value.indexOf(userId);

    if (currentIndex >= 0) {
      this.removeUserOrTeamFromProject(userId, null);
    } else {
      this.assignUserOrTeamToProject(userId, null);
    }
  }

  toggleTeam(teamId: string): void {
    const currentIndex = this.teamsFormArray.value.indexOf(teamId);

    if (currentIndex >= 0) {
      this.removeUserOrTeamFromProject(null, teamId);
    } else {
      this.assignUserOrTeamToProject(null, teamId);
    }
  }

  assignUserOrTeamToProject(userId: string, teamId: string): void {
    const body: {projectId: string; userId?: string; teamId?: string} = {
      projectId: this.data().projectId,
    };
    let id: string;
    if (userId) {
      body.userId = userId;
      id = userId;
    }
    if (teamId) {
      body.teamId = teamId;
      id = teamId;
    }
    this.loadingObject.set({...this.loadingObject(), [id]: true});
    this.store$.dispatch(
      new AssignUserOrTeamToProjectAction({
        ...body,
        onSuccess: () => {
          this.initialize();
          this.loadingObject.set({...this.loadingObject(), [id]: false});
          this.modifyUserTeamList.emit({type: userId ? 'user' : 'team', action: 'add', id});
        },
        onFailure: () => {
          this.loadingObject.set({...this.loadingObject(), [id]: false});
        },
      })
    );
  }

  removeUserOrTeamFromProject(userId: string, teamId: string, force: boolean = false): void {
    const body: {projectId: string; userId?: string; teamId?: string} = {
      projectId: this.data().projectId,
    };
    let id: string;
    let type: 'user' | 'team';
    if (userId) {
      body.userId = userId;
      id = userId;
      type = 'user';
    }
    if (teamId) {
      body.teamId = teamId;
      id = teamId;
      type = 'team';
    }
    this.loadingObject.set({...this.loadingObject(), [id]: true});
    this.store$.dispatch(
      new RemoveUserOrTeamFromProjectAction({
        ...body,
        force,
        onSuccess: () => {
          this.initialize();
          this.loadingObject.set({...this.loadingObject(), [id]: false});
          this.modifyUserTeamList.emit({type: type, action: 'remove', id});
        },
        onFailure: () => {
          this.loadingObject.set({...this.loadingObject(), [id]: false});
        },
        onConflict: (conflictResponse: RemoveUserConflictResponse) => {
          this.conflictResponse.set({...conflictResponse, type, id});
          this.loadingObject.set({...this.loadingObject(), [id]: false});
          this.showOverlay.set(true);
        },
      })
    );
  }

  closeOverlay(): void {
    this.showOverlay.set(false);
  }

  onConfirmRemove(): void {
    if (this.conflictResponse().type === 'user') {
      this.removeUserOrTeamFromProject(this.conflictResponse().id, null, true);
    } else {
      this.removeUserOrTeamFromProject(null, this.conflictResponse().id, true);
    }
    this.closeOverlay();
  }

  onAddPeople(): void {
    this.selectedOption.set('people');
  }
}
